/**
 * Rolling median of the last few readings for one string.
 *
 * A single analysis frame can be wrong: an overtone from a neighbouring
 * string, a transient at the moment of the pluck, a frame that caught the note
 * mid-decay. A median over a short history throws those away without lagging
 * the way an average would — one bad frame in five moves the result not at all,
 * whereas an average would drag it partway.
 */

/** How many frames the median spans. */
export const DEFAULT_HISTORY = 5

/**
 * Fewest readings needed before anything is reported.
 *
 * Requiring more than one is what stops a lone spurious frame from lighting
 * up a bar: a note has to persist to be believed.
 */
export const DEFAULT_MIN_READINGS = 3

/** Frequency history for a single string. */
export class MedianTracker {
  private readonly history: number[] = []
  private readonly scratch: number[] = []

  constructor(
    private readonly size: number = DEFAULT_HISTORY,
    private readonly minReadings: number = DEFAULT_MIN_READINGS,
  ) {
    if (size < 1) throw new RangeError('size must be at least 1')
    if (minReadings < 1) throw new RangeError('minReadings must be at least 1')
  }

  /** Number of readings currently held. */
  get length(): number {
    return this.history.length
  }

  /** Record one frequency, dropping the oldest once the window is full. */
  push(frequency: number): void {
    this.history.push(frequency)
    if (this.history.length > this.size) this.history.shift()
  }

  /**
   * Record that this frame had nothing for the string.
   *
   * A gap ages the history by one slot rather than clearing it, so a note that
   * flickers for a single frame keeps its value while one that has genuinely
   * stopped empties out after `size` frames.
   */
  pushSilence(): void {
    this.history.shift()
  }

  /** Forget everything, e.g. when the tuning changes. */
  clear(): void {
    this.history.length = 0
  }

  /**
   * Median of the history, or null while too few readings have arrived.
   */
  value(): number | null {
    const count = this.history.length
    if (count < this.minReadings) return null

    this.scratch.length = 0
    for (const entry of this.history) this.scratch.push(entry)
    this.scratch.sort((a, b) => a - b)

    const middle = count >> 1
    if (count % 2 === 1) return this.scratch[middle]!
    return (this.scratch[middle - 1]! + this.scratch[middle]!) / 2
  }
}
